import React, { useEffect, useState } from 'react'
import { View, Image, StyleSheet, useWindowDimensions } from 'react-native'
import { Text } from '@libs/components'
import { useUpdateProfile } from '@app-hero/shared-hooks'
import { useNavigation } from '@react-navigation/native'
import LottieView from 'react-native-web-lottie'
import { useAtom } from 'jotai'
import { CustomAuth } from '@libs/utils'
import LinearGradient from 'react-native-linear-gradient'
import { AppheroBeta, AppHeroLogoLite } from '@apphero/assets'
import { useTheme } from '@libs/theme'
import { AlertCircle, Check } from 'lucide-react'
import { t } from 'i18next'
import { isWeb } from '@libs/utils/src/screenLayout'
import { Icon } from '@app-hero/native-icons'
import {
  alertBoxAtom,
  errorMessageAtom,
  isChatbotVisible,
  ResetPasswordAtom,
  signUpDetailsAtom,
  userProfile,
} from '../../utils'
import { createCloudWatchLog } from '../../api'

const DesktopView = (props) => {
  const [errorMessage, setErrorMessage] = useAtom(errorMessageAtom)
  const [, setForgotPasswordCredential] = useAtom(ResetPasswordAtom)
  const [signUpDetails] = useAtom(signUpDetailsAtom)
  const [, setAlertBoxDetails] = useAtom(alertBoxAtom)
  const { mutation } = useUpdateProfile()
  const [, setUserDetails] = useAtom(userProfile)
  const [, setCanShowChatbot] = useAtom(isChatbotVisible)
  const navigation = useNavigation()
  const [, setIsLoading] = useState(false)
  const [showSucessMessage, setShowSuccessMessage] = useState(false)
  const { style, hasFlexValue = true, label, subText } = props
  const { colors } = useTheme()
  const { width } = useWindowDimensions()
  const isDesktop = isWeb(width)

  const brandLogoUrl = props?.brandLogoUrl

  const brandDetails = props?.brandDetails

  const LogoUrl = `${brandLogoUrl}/${brandDetails?.brand?.replaceAll(
    ' ',
    '_',
  )}.png`

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const codeParam = params.get('confirmation_code')
    const emailParam = params.get('email')

    if (codeParam && emailParam) {
      handleConfirmSignUp(codeParam, emailParam)
    }
  }, [])

  const handleConfirmSignUp = async (codeParam, emailParam) => {
    setIsLoading(true)

    const codeToUse = codeParam
    const emailToUse = emailParam || signUpDetails.email.toLowerCase()

    // Check if OTP contains any whitespace
    if (/\s/.test(codeToUse)) {
      setErrorMessage('Spaces are not allowed in OTP. Please try again.')
      // Clear the OTP input
      setForgotPasswordCredential((prevData) => ({
        ...prevData,
        code: '',
      }))
      setIsLoading(false)
      return
    }

    const userData = await CustomAuth.confirmSignUp(
      emailToUse,
      codeToUse,
      signUpDetails?.password,
    )

    if (userData?.message) {
      if (userData?.name === 'CodeMismatchException') {
        console.log(
          'Error occured CodeMismatchException:',
          userData?.message,
          userData?.name,
        )
        setErrorMessage(
          'The verification code you entered is incorrect. Please try again.',
        )
      } else if (userData?.name === 'ExpiredCodeException') {
        console.log('Error occured :', userData?.message, userData?.name)
        setErrorMessage(
          'The verification code you entered is incorrect. Please try again.',
        )
      } else if (
        userData?.message ===
        'User cannot be confirmed. Current status is CONFIRMED'
      ) {
        setErrorMessage('This account has already been verified.')
      } else if (userData?.name) {
        console.log('Error occured :', userData?.message)
        setErrorMessage(
          'The verification code you entered is incorrect. Please try again.',
        )
      } else {
        navigation.navigate('sign-up', { invalidUser: true })
        setAlertBoxDetails({
          visible: true,
          message: t('AUTH.UNKNOWN_ERROR_OTP'),
        })
      }

      const cloudWatchLoggerPayload = {
        logStream: `apphero-frontend-service/${emailToUse}`,
        payload: { email: emailToUse },
        response: userData,
        isErrorLog: true,
        errorMessage: userData?.message,
        event: 'FAILED_POST_SIGNUP',
        useCase: 'SIGNUP',
      }
      createCloudWatchLog(cloudWatchLoggerPayload)
      setIsLoading(false)
      setTimeout(() => {
        navigation.navigate('login')
        setErrorMessage('')
      }, 3000)
      return
    }
    if (userData && !userData?.message) {
      const cloudWatchLoggerPayload = {
        logStream: `apphero-frontend-service/${emailToUse}`,
        payload: { email: emailToUse },
        response: userData,
        isErrorLog: false,
        logMessage: `Post Signup Successfully completed for ${emailToUse}`,
        event: 'SUCCESS_POST_SIGNUP',
        useCase: 'SIGNUP',
      }

      createCloudWatchLog(cloudWatchLoggerPayload)

      setUserDetails((prev) => ({ ...prev, ...userData }))
      await mutation.mutateAsync({
        updatedProfile: signUpDetails,
        isInitial: true,
      })
      setShowSuccessMessage(true)

      setCanShowChatbot(true)
      if (userData?.canApply) {
        setTimeout(() => {
          setShowSuccessMessage(false)
          navigation.navigate('path-finder')
        }, 3000)
      } else {
        setTimeout(() => {
          setShowSuccessMessage(false)
          navigation.navigate('login')
        }, 3000)
      }
      setIsLoading(false)
    }
  }

  if (errorMessage) {
    return (
      <View
        style={[
          {
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: colors?.backgroundSecondary,
          },
          hasFlexValue ? { flex: 1 } : {},
          style,
        ]}
      >
        <View
          style={[
            styles.leftHeader,
            { flexDirection: 'row', alignItems: 'center' },
          ]}
        >
          <Image
            source={AppheroBeta}
            style={{ height: 38, width: 146 }}
            resizeMode="contain"
          />
          {brandDetails.brand && brandLogoUrl && (
            <>
              <View
                style={{
                  height: '1.15rem',
                  width: 2,
                  backgroundColor: colors.neutral,
                  marginHorizontal: '0.75rem',
                }}
              />
              <Image
                source={LogoUrl}
                style={{ height: 60, width: 146 }}
                resizeMode="contain"
                alt={brandDetails.brand}
              />
            </>
          )}
        </View>
        <View
          style={[
            styles.cardContainer(colors),
            {
              maxHeight: '30rem',
              width: '30rem',
              paddingBottom: '3rem',
            },
          ]}
        >
          <View
            style={{
              backgroundColor: '#C00F0C',
              width: 64,
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 48,
              marginTop: '2.5rem',
            }}
          >
            <AlertCircle
              style={{ width: 32, height: 32, color: colors.white }}
            />
          </View>
          <Text
            variant="heading3"
            style={{ marginTop: '1.5rem', textAlign: 'center', padding: 20 }}
            color={colors.titleColor}
          >
            {errorMessage}
          </Text>

          <Text
            variant="display3"
            style={{ marginTop: '1.5rem', textAlign: 'center' }}
            color={colors.titleColor}
          >
            Redirecting you to login...
          </Text>
        </View>
      </View>
    )
  }

  return isDesktop ? (
    <View
      style={[
        {
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors?.backgroundSecondary,
        },
        hasFlexValue ? { flex: 1 } : {},
        style,
      ]}
    >
      <View
        style={[
          styles.leftHeader,
          { flexDirection: 'row', alignItems: 'center' },
        ]}
      >
        <Image
          source={AppheroBeta}
          style={{ height: 38, width: 146 }}
          resizeMode="contain"
        />
        {brandDetails.brand && brandLogoUrl && (
          <>
            <View
              style={{
                height: '1.15rem',
                width: 2,
                backgroundColor: colors.neutral,
                marginHorizontal: '0.75rem',
              }}
            />
            <Image
              source={LogoUrl}
              style={{ height: 60, width: 146 }}
              resizeMode="contain"
              alt={brandDetails.brand}
            />
          </>
        )}
      </View>
      {showSucessMessage ? (
        <View
          style={[
            styles.cardContainer(colors),
            {
              maxHeight: '30rem',
              width: '30rem',
              paddingBottom: '3rem',
            },
          ]}
        >
          <View
            style={{
              backgroundColor: '#32CD32',
              width: 64,
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 48,
              marginTop: '2.5rem',
            }}
          >
            <Check style={{ width: 32, height: 32, color: colors.white }} />
          </View>
          <Text
            variant="heading3"
            style={{
              marginTop: '1.5rem',
              textAlign: 'center',
            }}
            color={colors.titleColor}
          >
            Your account has been successfully verified!
          </Text>

          <Text
            variant="display3"
            style={{ marginTop: '1.5rem', textAlign: 'center' }}
            color={colors.titleColor}
          >
            Redirecting you to login...
          </Text>
        </View>
      ) : (
        <View
          style={[
            styles.cardContainer(colors),
            {
              maxHeight: '30rem',
              width: '30rem',
            },
          ]}
        >
          <Icon
            name="AuthLoading"
            style={{
              height: 152,
              width: 132,
              marginTop: '2.5rem',
            }}
          />
          <Text
            variant="heading3"
            style={{ marginTop: '1.5rem' }}
            color={colors.titleColor}
          >
            {label}
          </Text>
          {subText && (
            <Text
              variant="display4"
              style={{
                marginLeft: 30,
                marginRight: 30,
                marginTop: 12,
                textAlign: 'center',
              }}
              color={colors.titleColor}
            >
              {subText}
            </Text>
          )}
          <View style={styles.animationContainer}>
            <LottieView
              source={require('../../../../../libs/components/src/Loader/loadingAnimation.json')}
              autoPlay
              loop
              speed={0.8}
            />
          </View>
        </View>
      )}
    </View>
  ) : (
    <LinearGradient
      style={[
        {
          justifyContent: 'center',
          backgroundColor: colors?.primaryBackground,
          paddingLeft: 20,
          paddingRight: 20,

          minHeight: '100vh',
          paddingBottom: 80,
        },
        style,
      ]}
      start={{ x: 0, y: 0 }}
      end={{ x: -1, y: -1 }}
      colors={['#2563EB', '#18181B']}
      locations={[0.2, 1.3]}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Image
          source={AppHeroLogoLite}
          style={{ height: 38, width: 146 }}
          resizeMode="contain"
        />
        {brandDetails.brand && brandLogoUrl && (
          <>
            <View
              style={{
                height: '1.15rem',
                width: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.4)',
                marginHorizontal: '0.75rem',
              }}
            />
            <Image
              source={`${brandLogoUrl}/${brandDetails?.brand?.replaceAll(
                ' ',
                '_',
              )}_LIGHT.png`}
              style={{ height: 60, width: 146 }}
              resizeMode="contain"
              alt={brandDetails.brand}
            />
          </>
        )}
      </View>
      <View
        style={[
          styles.cardContainer(colors),
          { width: 327, marginHorizontal: 'auto' },
        ]}
      >
        <Icon
          name="AuthLoading"
          style={{
            height: 152,
            width: 132,
            marginTop: 92,
          }}
        />
        <Text
          variant="heading3"
          style={{ paddingTop: 32, paddingBottom: 24, width: '70%' }}
          numberOfLines={2}
          textAlign="center"
        >
          {label}
        </Text>
        {subText && (
          <Text
            variant="display4"
            style={{ width: '70%', marginTop: 12 }}
            numberOfLines={2}
            textAlign="center"
          >
            {subText}
          </Text>
        )}
        <View style={styles.animationContainer}>
          <LottieView
            source={require('../../../../../libs/components/src/Loader/loadingAnimation.json')}
            autoPlay
            loop
            speed={0.8}
          />
        </View>
      </View>
    </LinearGradient>
  )
}

export default DesktopView

const styles = StyleSheet.create({
  animationContainer: {
    height: 123,
    width: 170,
  },
  cardContainer: (color) => ({
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: color?.card,
    borderRadius: 16,
    shadowColor: 'rgba(0, 0, 0, 0.1)', // Black shadow
    shadowOffset: { width: 0, height: 6 }, // Adjust shadow offset
    shadowOpacity: 0.25, // Adjust opacity (0-1 range)
    shadowRadius: 8, // Blur radius of the shadow
    elevation: 5,
  }),
  leftHeader: {
    position: 'absolute',
    zIndex: 20,
    top: '1.25rem',
    left: '5.625rem',
  },
})
